import { FC, useEffect, useMemo, useState } from 'react'
import { BaseProps } from '@/types/common'
import { i18n } from '@/lang'
import { SHOP_PRODUCTS, TEA_PRODUCT_NAMES } from '@/constants/products'
import { productsApi } from '@/api'
import ProductCard from './ProductCard'
import { SearchInput } from '@/components/shared/SearchInput'

interface ProductGridProps extends BaseProps {
  priceRange?: { min: number; max: number }
  selectedCategories?: string[]
  selectedFormats?: string[]
  searchQuery?: string
  onSearch?: (query: string) => void
}

const ProductGrid: FC<ProductGridProps> = ({ 
  lang, 
  priceRange,
  selectedCategories = [],
  selectedFormats = [],
  searchQuery = '',
  onSearch,
  className = '' 
}) => {
  const fontClass = lang === 'zh' ? 'font-zh' : 'font-en'
  const text = i18n[lang]
  const [catalogProducts, setCatalogProducts] = useState(SHOP_PRODUCTS)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let active = true

    void productsApi.getCatalogProducts()
      .then((products) => {
        if (active && products.length > 0) {
          setCatalogProducts(products)
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  // Filter products based on criteria
  const filteredProducts = useMemo(() => {
    return catalogProducts.filter(product => {
      // Search filter
      if (searchQuery) {
        const productName = TEA_PRODUCT_NAMES[product.key]?.[lang] || ''
        const lowerQuery = searchQuery.toLowerCase()
        const lowerName = productName.toLowerCase()
        if (!lowerName.includes(lowerQuery)) {
          return false
        }
      }

      // Price filter
      if (priceRange) {
        if (product.price < priceRange.min || product.price > priceRange.max) {
          return false
        }
      }

      // Category filter
      if (selectedCategories.length > 0 && product.category) {
        if (!selectedCategories.includes(product.category)) {
          return false
        }
      }

      // Format filter
      if (selectedFormats.length > 0 && product.format) {
        if (!selectedFormats.includes(product.format)) {
          return false
        }
      }

      return true
    })
  }, [catalogProducts, searchQuery, priceRange, selectedCategories, selectedFormats, lang])

  const resultText = text.showingResults.replace('{count}', filteredProducts.length.toString())

  return (
    <div className={`w-full ${fontClass} ${className}`}>
      {/* Results Header */}
      <div className="mb-6 flex items-center justify-between">
        <p className="text-gray-600">{resultText}</p>
        <div className="w-80">
          <SearchInput 
            lang={lang} 
            variant="expandable"
            onSearch={onSearch || (() => {})}
            placeholder={lang === 'zh' ? '搜尋商品...' : 'Search products...'}
          />
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white overflow-hidden shadow-md">
                {/* Image skeleton */}
                <div className="aspect-[4/3] animate-pulse bg-gray-200" />
                {/* Info skeleton */}
                <div className="p-4 flex flex-col gap-3">
                  {/* Tags */}
                  <div className="flex gap-2">
                    <div className="animate-pulse bg-gray-200 h-5 w-16 rounded" />
                    <div className="animate-pulse bg-gray-200 h-5 w-12 rounded" />
                  </div>
                  {/* Name and price row */}
                  <div className="flex items-center justify-between">
                    <div className="animate-pulse bg-gray-200 h-4 w-28 rounded" />
                    <div className="animate-pulse bg-gray-200 h-5 w-14 rounded" />
                  </div>
                </div>
              </div>
            ))
          : filteredProducts.map((product, index) => (
              <ProductCard
                key={`${product.key}-${product.format}-${index}`}
                lang={lang}
                product={product}
              />
            ))}
      </div>

      {/* No Results */}
      {!isLoading && filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {lang === 'zh' ? '沒有找到符合條件的商品' : 'No products found matching your criteria'}
          </p>
        </div>
      )}
    </div>
  )
}

export default ProductGrid
